/**
 * Search and Filter Utilities
 * Centralized utilities for search functionality across the application
 */

/**
 * Generic search function for filtering arrays of objects
 */
export function searchItems(items, searchTerm, searchFields) {
  if (!searchTerm || !items || items.length === 0) {
    return items || [];
  }

  const lowercaseSearchTerm = searchTerm.toLowerCase();
  
  return items.filter(item => {
    return searchFields.some(field => {
      const value = getNestedValue(item, field);
      if (value === null || value === undefined) return false;
      
      return String(value).toLowerCase().includes(lowercaseSearchTerm);
    });
  });
}

/**
 * Get nested value from object using dot notation
 */
function getNestedValue(obj, path) {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : null;
  }, obj);
}

/**
 * Filter items by date range
 */
export function filterByDateRange(items, dateField, startDate, endDate) {
  if (!items || items.length === 0) return [];
  if (!startDate && !endDate) return items;

  const start = startDate ? new Date(startDate) : null;
  const end = endDate ? new Date(endDate) : null;

  return items.filter(item => {
    const itemDate = new Date(getNestedValue(item, dateField));
    if (isNaN(itemDate.getTime())) return true; // Invalid date, include item
    
    if (start && itemDate < start) return false;
    if (end && itemDate > end) return false;
    
    return true;
  });
}

/**
 * Filter items by category
 */
export function filterByCategory(items, categoryField, selectedCategories) {
  if (!items || items.length === 0) return [];
  if (!selectedCategories || selectedCategories.length === 0) return items;

  return items.filter(item => {
    const itemCategory = getNestedValue(item, categoryField);
    return selectedCategories.includes(itemCategory);
  });
}

/**
 * Sort items by field
 */
export function sortItems(items, sortField, sortDirection = 'asc') {
  if (!items || items.length === 0) return [];

  return [...items].sort((a, b) => {
    const aVal = getNestedValue(a, sortField);
    const bVal = getNestedValue(b, sortField);
    
    // Handle null/undefined values
    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;
    
    // Handle different data types
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }
    
    if (aVal instanceof Date && bVal instanceof Date) {
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }
    
    // String comparison
    const comparison = String(aVal).localeCompare(String(bVal));
    return sortDirection === 'asc' ? comparison : -comparison;
  });
}

/**
 * Paginate items
 */
export function paginateItems(items, page, itemsPerPage) {
  if (!items || items.length === 0) {
    return {
      items: [],
      totalPages: 0,
      currentPage: 1,
      totalItems: 0
    };
  }

  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = items.slice(startIndex, endIndex);

  return {
    items: paginatedItems,
    totalPages: Math.ceil(items.length / itemsPerPage),
    currentPage: page,
    totalItems: items.length
  };
}

/**
 * Debounced search function
 */
export function createDebouncedSearch(callback, delay = 300) {
  let timeoutId;
  
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => callback(...args), delay);
  };
}

/**
 * Simple debounce function
 */
export function debounce(callback, delay = 300) {
  let timeoutId;
  
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => callback(...args), delay);
  };
}

/**
 * Get unique values from array of objects for a specific field
 */
export function getUniqueValues(items, field) {
  if (!items || items.length === 0) return [];
  
  const values = items.map(item => getNestedValue(item, field))
    .filter(value => value !== null && value !== undefined);
    
  return [...new Set(values)];
}

/**
 * Advanced filter function for multiple criteria
 */
export function advancedFilter(items, filters) {
  if (!items || items.length === 0) return [];
  if (!filters || Object.keys(filters).length === 0) return items;

  return items.filter(item => {
    return Object.entries(filters).every(([key, filterValue]) => {
      if (filterValue === null || filterValue === undefined || filterValue === '') {
        return true; // No filter applied
      }

      const itemValue = getNestedValue(item, key);

      // Handle different filter types
      if (Array.isArray(filterValue)) {
        return filterValue.includes(itemValue);
      }

      if (typeof filterValue === 'object' && filterValue.type) {
        switch (filterValue.type) {
          case 'range':
            return itemValue >= filterValue.min && itemValue <= filterValue.max;
          case 'greater':
            return itemValue > filterValue.value;
          case 'less':
            return itemValue < filterValue.value;
          case 'contains':
            return String(itemValue).toLowerCase().includes(String(filterValue.value).toLowerCase());
          default:
            return itemValue === filterValue.value;
        }
      }

      return itemValue === filterValue;
    });
  });
}

/**
 * Search highlight utility
 */
export function highlightSearchTerm(text, searchTerm) {
  if (!searchTerm || !text) return text;

  const regex = new RegExp(`(${escapeRegExp(searchTerm)})`, 'gi');
  return text.replace(regex, '<mark>$1</mark>');
}

/**
 * Escape special regex characters
 */
function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Create search suggestions
 */
export function createSearchSuggestions(items, fields, searchTerm, maxSuggestions = 5) {
  if (!searchTerm || searchTerm.length < 2) return [];

  const suggestions = new Set();
  const lowercaseSearchTerm = searchTerm.toLowerCase();

  items.forEach(item => {
    fields.forEach(field => {
      const value = getNestedValue(item, field);
      if (value && String(value).toLowerCase().includes(lowercaseSearchTerm)) {
        suggestions.add(String(value));
      }
    });
  });

  return Array.from(suggestions).slice(0, maxSuggestions);
}

/**
 * Smart search that handles multiple terms and search operators
 */
export function smartSearch(items, searchQuery, searchFields) {
  if (!searchQuery || !items || items.length === 0) {
    return items || [];
  }

  // Parse search query for operators
  const terms = searchQuery.toLowerCase().split(' ').filter(term => term.length > 0);
  
  return items.filter(item => {
    const itemText = searchFields.map(field => {
      const value = getNestedValue(item, field);
      return value ? String(value).toLowerCase() : '';
    }).join(' ');

    // All terms must be found in the item text
    return terms.every(term => {
      if (term.startsWith('-')) {
        // Exclude term (NOT operator)
        return !itemText.includes(term.substring(1));
      }
      
      if (term.startsWith('+')) {
        // Required term (AND operator)
        return itemText.includes(term.substring(1));
      }
      
      // Regular term
      return itemText.includes(term);
    });
  });
}